/**
 * Página principal de análise de transcrição
 * Integra todos os componentes: viewer, métricas, resumo IA e exportação
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Skeleton,
  keyframes,
  useMediaQuery,
  useTheme
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Transcription, AISummary, TranscriptionMetrics, UserAnnotations } from '../../../types/transcription'
import { getTranscription, updateSpeakerMapping, getAnnotations, updateAnnotations, getAnalysisStatus, AnalysisStatus } from '../../../services/api'
import { SpeakerMapping } from '../../../services/exportService'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import BetaBadge from '../../../components/BetaBadge'
import TranscriptViewer from './TranscriptViewerHybrid'
import MetricsDashboard from './MetricsDashboard'
import AISummaryPanel from './AISummaryPanel'
import ExportPanel from './ExportPanel'
import SentimentAnalysisPanel from './SentimentAnalysisPanel'
import EntityExtractionPanel from './EntityExtractionPanel'
import TopicDetectionPanel from './TopicDetectionPanel'
import EventTimelinePanel from './EventTimelinePanel'
import SpeechPatternPanel from './SpeechPatternPanel'
import MediaPlayer from './MediaPlayer'
import useMediaPlayer from '../hooks/useMediaPlayer'
import { formatDuration } from '../../../services/transcriptionAnalysis'

// Animação de ondas de áudio
const audioWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  25% { transform: scaleY(1); }
  50% { transform: scaleY(0.5); }
  75% { transform: scaleY(0.8); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

type TabValue = 'transcript' | 'metrics' | 'sentiment' | 'entities' | 'topics' | 'timeline' | 'patterns' | 'ai' | 'export'

type Props = {
  fileId: string
  fileName: string
  fileType?: 'AUDIO' | 'VIDEO'
  processingTimes?: {
    createdAt?: string | number | null
    processingStartedAt?: number | null
    transcriptionCompletedAt?: number | null
    analysisCompletedAt?: number | null
  }
  onBack: () => void
  onRename?: () => void
}

interface TabPanelProps {
  children?: React.ReactNode
  value: TabValue
  current: TabValue
  fullHeight?: boolean
}

// Memoized TabPanel para evitar re-renders desnecessários
const TabPanel = React.memo(function TabPanel({ children, value, current, fullHeight = false }: TabPanelProps) {
  const isActive = value === current

  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      sx={{
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column',
        pt: 2,
        // fullHeight só aplica altura fixa para tabs que não usam window scroll
        ...(fullHeight && {
          minHeight: 400
        })
      }}
    >
      {isActive ? children : null}
    </Box>
  )
})

export default function AnalysisPage({ fileId, fileName, fileType, processingTimes, onBack, onRename }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Media player para reprodução sincronizada com transcrição
  const mediaPlayer = useMediaPlayer(fileId)

  const [currentTab, setCurrentTab] = useState<TabValue>('transcript')
  const [transcription, setTranscription] = useState<Transcription | null>(null)
  const [metrics, setMetrics] = useState<TranscriptionMetrics | null>(null)
  const [aiSummary, setAiSummary] = useState<AISummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [speakerMapping, setSpeakerMapping] = useState<SpeakerMapping>({})
  const [isSavingMapping, setIsSavingMapping] = useState(false)
  
  // Estado para anotações
  const [annotations, setAnnotations] = useState<UserAnnotations>({ items: [], highlights: [], bookmarks: [] })
  const [isSavingAnnotations, setIsSavingAnnotations] = useState(false)
  const saveAnnotationsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialAnnotationsLoadedRef = useRef(false)
  const annotationsUserModifiedRef = useRef(false)
  
  // Estado para status de análise (polling)
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus | null>(null)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  
  // Estado para navegação de segmento externo (das abas de análise)
  const [scrollToSegmentIndex, setScrollToSegmentIndex] = useState<number | null>(null)

  // Player começa desativado — o usuário habilita via botão na barra da transcrição
  const [playerEnabled, setPlayerEnabled] = useState(false)

  // Auto-pause/resume: pausa o áudio durante ações de edição/nota e retoma ao concluir
  const wasPlayingBeforeActionRef = useRef(false)
  const actionDepthRef = useRef(0)

  const handlePauseForAction = useCallback(() => {
    actionDepthRef.current++
    if (actionDepthRef.current === 1 && mediaPlayer.isPlaying) {
      wasPlayingBeforeActionRef.current = true
      mediaPlayer.pause()
    }
  }, [mediaPlayer])

  const handleResumeAfterAction = useCallback(() => {
    actionDepthRef.current = Math.max(0, actionDepthRef.current - 1)
    if (actionDepthRef.current === 0 && wasPlayingBeforeActionRef.current) {
      wasPlayingBeforeActionRef.current = false
      mediaPlayer.play()
    }
  }, [mediaPlayer])

  // Índice do segmento ativo durante reprodução de mídia
  const activeSegmentIndex = useMemo(() => {
    if (!transcription?.transcript || mediaPlayer.currentTime <= 0) return null
    const idx = transcription.transcript.findIndex(s => {
      const start = parseFloat(s.start_time)
      const end = parseFloat(s.end_time)
      return mediaPlayer.currentTime >= start && mediaPlayer.currentTime < end
    })
    return idx >= 0 ? idx : null
  }, [transcription?.transcript, mediaPlayer.currentTime])

  // Índice do segmento em reprodução no modo "play segment"
  const playingSegmentIndex = useMemo(() => {
    if (!transcription?.transcript || mediaPlayer.segmentPlayingEndTime === null) return null
    const idx = transcription.transcript.findIndex(s => {
      const end = parseFloat(s.end_time)
      return Math.abs(end - mediaPlayer.segmentPlayingEndTime!) < 0.01
    })
    return idx >= 0 ? idx : null
  }, [transcription?.transcript, mediaPlayer.segmentPlayingEndTime])

  // Handler para reproduzir trecho de um segmento
  const handlePlaySegment = useCallback((startTime: number, endTime: number) => {
    // Se o player não está habilitado, habilita-o antes de reproduzir
    if (!playerEnabled) {
      setPlayerEnabled(true)
    }
    // Pequeno delay para garantir que o player está montado ao habilitar pela primeira vez
    setTimeout(() => {
      mediaPlayer.playSegment(startTime, endTime)
    }, playerEnabled ? 0 : 150)
  }, [playerEnabled, mediaPlayer])
  
  // Referência para debounce da atualização do speakerMapping
  const saveMappingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialMappingLoadedRef = useRef(false)
  const mappingUserModifiedRef = useRef(false)

  // Função para navegar a um segmento específico na transcrição
  const handleNavigateToSegment = useCallback((segmentIndex: number) => {
    // Muda para a aba de transcrição
    setCurrentTab('transcript')
    // Define o índice do segmento para scroll (usa timestamp para garantir que o useEffect seja acionado mesmo se o índice for o mesmo)
    setScrollToSegmentIndex(segmentIndex)
    // Reset após navegação para permitir navegações repetidas ao mesmo segmento
    setTimeout(() => setScrollToSegmentIndex(null), 200)
  }, [])

  // Wrapper para setSpeakerMapping que marca modificação do usuário
  const handleSpeakerMappingChange = useCallback((newMapping: SpeakerMapping) => {
    mappingUserModifiedRef.current = true
    setSpeakerMapping(newMapping)
  }, [])

  // Wrapper para setAnnotations que marca modificação do usuário
  const handleAnnotationsChange = useCallback((newAnnotations: UserAnnotations) => {
    annotationsUserModifiedRef.current = true
    setAnnotations(newAnnotations)
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    initialMappingLoadedRef.current = false
    initialAnnotationsLoadedRef.current = false
    mappingUserModifiedRef.current = false
    annotationsUserModifiedRef.current = false
    try {
      // Chama API real do backend monkee-ai-api
      const response = await getTranscription(fileId)
      setTranscription(response.transcription)
      setMetrics(response.metrics)
      
      // Carrega speakerMapping do backend se disponível
      if (response.transcription.speakerMapping) {
        setSpeakerMapping(response.transcription.speakerMapping)
      }
      initialMappingLoadedRef.current = true
      
      // Carrega anotações do backend
      try {
        const annotationsData = await getAnnotations(fileId)
        if (annotationsData) {
          setAnnotations(annotationsData)
        }
      } catch (annErr) {
        console.log('Sem anotações existentes:', annErr)
      }
      initialAnnotationsLoadedRef.current = true
    } catch (e: any) {
      const friendlyError = contextualErrors.transcription(e)
      setError(friendlyError)
      console.error('Erro ao carregar transcrição:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [fileId])

  // Salva speakerMapping no backend com debounce quando alterado PELO USUÁRIO
  useEffect(() => {
    // Não salvar antes do carregamento inicial, se está carregando, ou se não foi modificado pelo usuário
    if (!initialMappingLoadedRef.current || loading || !mappingUserModifiedRef.current) {
      return
    }

    // Cancelar timeout anterior
    if (saveMappingTimeoutRef.current) {
      clearTimeout(saveMappingTimeoutRef.current)
    }

    // Debounce de 1 segundo antes de salvar
    saveMappingTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSavingMapping(true)
        await updateSpeakerMapping(fileId, speakerMapping)
        console.log('Speaker mapping salvo com sucesso')
      } catch (e) {
        console.error('Erro ao salvar speaker mapping:', e)
        // Não mostramos erro ao usuário para não interromper o fluxo
      } finally {
        setIsSavingMapping(false)
      }
    }, 1000)

    // Cleanup
    return () => {
      if (saveMappingTimeoutRef.current) {
        clearTimeout(saveMappingTimeoutRef.current)
      }
    }
  }, [speakerMapping, fileId, loading])

  // Salva anotações no backend com debounce quando alteradas PELO USUÁRIO
  useEffect(() => {
    // Não salvar antes do carregamento inicial, se está carregando, ou se não foi modificado pelo usuário
    if (!initialAnnotationsLoadedRef.current || loading || !annotationsUserModifiedRef.current) {
      return
    }

    // Cancelar timeout anterior
    if (saveAnnotationsTimeoutRef.current) {
      clearTimeout(saveAnnotationsTimeoutRef.current)
    }

    // Debounce de 800ms antes de salvar
    saveAnnotationsTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSavingAnnotations(true)
        await updateAnnotations(fileId, annotations)
        console.log('Anotações salvas com sucesso')
      } catch (e) {
        console.error('Erro ao salvar anotações:', e)
      } finally {
        setIsSavingAnnotations(false)
      }
    }, 800)

    // Cleanup
    return () => {
      if (saveAnnotationsTimeoutRef.current) {
        clearTimeout(saveAnnotationsTimeoutRef.current)
      }
    }
  }, [annotations, fileId, loading])

  // Polling de status de análise
  useEffect(() => {
    // Só inicia polling se a transcrição foi carregada
    if (!transcription || loading) return

    const checkStatus = async () => {
      try {
        const status = await getAnalysisStatus(fileId)
        setAnalysisStatus(status)
        
        // Se tudo está completo ou falhou, para o polling
        if (status.overall === 'COMPLETED' || status.overall === 'FAILED') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }
      } catch (e) {
        console.error('Erro ao verificar status:', e)
      }
    }

    // Primeira verificação imediata
    checkStatus()

    // Polling a cada 2 segundos
    pollingIntervalRef.current = setInterval(checkStatus, 2000)

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [fileId, transcription, loading])

  // Pausar player ao desmontar o componente (sair da tela de análise)
  useEffect(() => {
    return () => {
      mediaPlayer.pause()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper para verificar se uma análise está pronta
  const isAnalysisReady = (status: string | undefined): boolean => {
    return status === 'COMPLETED'
  }
  
  // Helper para obter ícone de status
  const getStatusIcon = (status: string | undefined) => {
    if (status === 'COMPLETED') {
      return <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', ml: 0.5 }} />
    }
    if (status === 'FAILED') {
      return <ErrorIcon sx={{ fontSize: 14, color: 'error.main', ml: 0.5 }} />
    }
    if (status === 'PROCESSING') {
      return <CircularProgress size={12} sx={{ ml: 0.5 }} />
    }
    if (status === 'SKIPPED') {
      return (
        <Tooltip title="Análise não solicitada — pode ser executada sob demanda">
          <WarningAmberIcon sx={{ fontSize: 14, color: '#f57c00', ml: 0.5 }} />
        </Tooltip>
      )
    }
    return null // PENDING
  }

  // Helper para verificar se alguma análise individual ainda está processando
  const hasAnyProcessing = (s: AnalysisStatus): boolean => {
    return [s.sentiment, s.entities, s.topics, s.patterns, s.summary, s.timeline]
      .some(st => st === 'PROCESSING')
  }

  // Callback para forçar atualização do status quando uma análise on-demand é executada
  const handleOnDemandAnalysisStarted = useCallback(async () => {
    try {
      const status = await getAnalysisStatus(fileId)
      setAnalysisStatus(status)
      // Reinicia o polling para acompanhar o progresso da análise individual
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const s = await getAnalysisStatus(fileId)
          setAnalysisStatus(s)
          // Para o polling quando nenhuma análise individual está processando
          if (!hasAnyProcessing(s)) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current)
              pollingIntervalRef.current = null
            }
          }
        } catch (e) {
          console.error('Erro ao verificar status:', e)
        }
      }, 2000)
    } catch (e) {
      console.error('Erro ao atualizar status:', e)
    }
  }, [fileId])

  // Callback quando resumo IA é gerado
  const handleSummaryGenerated = (summary: AISummary) => {
    setAiSummary(summary)
  }

  // Fechar player: reseta áudio (volta ao início) e desabilita
  const handleClosePlayer = useCallback(() => {
    mediaPlayer.reset()
    setPlayerEnabled(false)
  }, [mediaPlayer])

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header skeleton */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              border: '1px solid #e0e0e0',
              animation: `${fadeIn} 0.3s ease-out`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="30%" height={20} />
              </Box>
            </Box>
          </Paper>

          {/* Loading animation */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 6, 
              border: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              animation: `${fadeIn} 0.5s ease-out`
            }}
          >
            {/* Ícone com animação de ondas */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: fileType === 'VIDEO' ? '#e3f2fd' : '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                position: 'relative'
              }}
            >
              {fileType === 'VIDEO' ? (
                <VideoFileIcon sx={{ fontSize: 48, color: '#1976d2' }} />
              ) : (
                <AudioFileIcon sx={{ fontSize: 48, color: '#f57c00' }} />
              )}
              
              {/* Ondas de áudio animadas */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '3px',
                  height: 24
                }}
              >
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      height: '100%',
                      bgcolor: fileType === 'VIDEO' ? '#1976d2' : '#f57c00',
                      borderRadius: '2px',
                      animation: `${audioWave} 1s ease-in-out infinite`,
                      animationDelay: `${i * 0.1}s`,
                      transformOrigin: 'bottom'
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Carregando análise...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {fileName}
            </Typography>

            {/* Barra de progresso */}
            <Box sx={{ width: '100%', maxWidth: 300 }}>
              <LinearProgress 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: fileType === 'VIDEO' ? '#1976d2' : '#f57c00',
                    borderRadius: 3
                  }
                }} 
              />
            </Box>

            {/* Etapas do loading */}
            <Box sx={{ mt: 4, display: 'flex', gap: 4 }}>
              {[
                { icon: <GraphicEqIcon />, label: 'Transcrição' },
                { icon: <AccessTimeIcon />, label: 'Métricas' },
                { icon: <AudioFileIcon />, label: 'Análise IA' }
              ].map((step, i) => (
                <Box 
                  key={i}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    opacity: 0.6
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.5,
                      color: '#666'
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {step.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>
    )
  }

  if (!transcription || error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            border: '1px solid #e0e0e0',
            bgcolor: '#fafafa'
          }}
        >
          {/* Ícone */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <Box sx={{ fontSize: 40 }}>⚠️</Box>
          </Box>
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error?.title || 'Erro ao carregar transcrição'}
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#666', mb: 2, maxWidth: 450, mx: 'auto' }}>
            {error?.message || 'Não foi possível carregar os dados desta transcrição.'}
          </Typography>
          
          {error?.suggestion && (
            <Typography variant="body2" sx={{ color: '#888', mb: 3, fontStyle: 'italic' }}>
              💡 {error.suggestion}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={onBack}>
              Voltar
            </Button>
            {error?.canRetry && (
              <Button variant="contained" onClick={loadData}>
                Tentar novamente
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    )
  }

  const durationSeconds = parseFloat(transcription.duration) * 60 // duration está em minutos

  // Detectar contexto WordPress para ajustar layout
  const isWordPress = typeof (window as any).monkeeTranscription !== 'undefined' || 
    !!document.querySelector('meta[name="generator"][content*="WordPress"]') ||
    typeof (window as any).wp !== 'undefined'

  // Player flutuante visível apenas na aba de transcrição quando mídia disponível E habilitado pelo usuário
  const showFloatingPlayer = playerEnabled && currentTab === 'transcript' && (mediaPlayer.mediaAvailable || mediaPlayer.isLoading)

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: isWordPress ? 'auto' : '100vh', pb: showFloatingPlayer ? '100px' : 4 }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Header */}
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
          <Grid container alignItems="center" spacing={{ xs: 1, sm: 2 }}>
            <Grid item>
              <Tooltip title="Voltar para arquivos" disableHoverListener={isMobile}>
                <IconButton onClick={onBack} size={isMobile ? 'small' : 'medium'}>
                  <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item>
              <Box
                sx={{
                  width: { xs: 36, sm: 48 },
                  height: { xs: 36, sm: 48 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  bgcolor: fileType === 'VIDEO' ? '#e3f2fd' : '#fff3e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {fileType === 'VIDEO' ? (
                  <VideoFileIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 28 } }} />
                ) : (
                  <AudioFileIcon sx={{ color: '#e65100', fontSize: { xs: 20, sm: 28 } }} />
                )}
              </Box>
            </Grid>

            <Grid item xs sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', sm: '1.25rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {fileName}
                </Typography>
                {onRename && (
                  <Tooltip title="Renomear transcrição" disableHoverListener={isMobile}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation()
                        onRename()
                      }}
                      sx={{
                        flexShrink: 0,
                        color: '#666',
                        '&:hover': {
                          color: '#2e7d32',
                          bgcolor: 'rgba(46, 125, 50, 0.08)'
                        }
                      }}
                    >
                      <EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, color: '#666', flexWrap: 'wrap' }}>
                <AccessTimeIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {isMobile ? formatDuration(parseFloat(transcription.duration)) : `Duração: ${formatDuration(parseFloat(transcription.duration))}`}
                </Typography>
                <Typography variant="body2" sx={{ mx: { xs: 0.25, sm: 1 }, display: { xs: 'none', sm: 'block' } }}>•</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
                  {transcription.transcript.length} segmentos
                </Typography>
                <Typography variant="body2" sx={{ mx: { xs: 0.25, sm: 1 }, display: { xs: 'none', sm: 'block' } }}>•</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
                  {transcription.participants.length} participantes
                </Typography>
              </Box>
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, mt: 1 }}>
                <BetaBadge />
              </Box>
            </Grid>

            {/* Botão "Voltar" apenas em desktop */}
            {!isMobile && (
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BetaBadge />
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ textTransform: 'none' }}
                  >
                    Voltar
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper elevation={0} sx={{
          border: '1px solid #e0e0e0',
          bgcolor: '#fff',
          '--monkee-bg': '#fff',
          borderRadius: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
          '#monkee-file-transcript-client &': {
            '--monkee-bg': '#fff',
            borderRadius: '12px !important',
            overflow: 'hidden !important',
            backgroundColor: '#fff !important',
            background: '#fff !important',
          },
        }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => {
              // Ao trocar de aba, fecha e reseta o player completamente
              if (newValue !== 'transcript' && playerEnabled) {
                mediaPlayer.reset()
                setPlayerEnabled(false)
              }
              setCurrentTab(newValue)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #e0e0e0',
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'auto'
              }
            }}
          >
            <Tab value="transcript" label="📝 Transcrição" />
            <Tab value="metrics" label="📊 Métricas" />
            <Tab
              value="sentiment"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  😊 Sentimento
                  {getStatusIcon(analysisStatus?.sentiment)}
                </Box>
              }
            />
            <Tab
              value="entities"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  🔍 Entidades
                  {getStatusIcon(analysisStatus?.entities)}
                </Box>
              }
            />
            <Tab
              value="topics"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  📑 Tópicos
                  {getStatusIcon(analysisStatus?.topics)}
                </Box>
              }
            />
            <Tab
              value="timeline"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  📅 Timeline
                  {getStatusIcon(analysisStatus?.timeline)}
                </Box>
              }
            />
            <Tab
              value="patterns"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  🎙️ Padrões
                  {getStatusIcon(analysisStatus?.patterns)}
                </Box>
              }
            />
            <Tab
              value="ai"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  🤖 Resumo IA
                  {getStatusIcon(analysisStatus?.summary)}
                </Box>
              }
            />
            <Tab value="export" label="📥 Exportar" />
          </Tabs>

          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
            <TabPanel value="transcript" current={currentTab}>
              <TranscriptViewer
                transcription={transcription}
                speakerMapping={speakerMapping}
                onSpeakerMappingChange={handleSpeakerMappingChange}
                onTimestampClick={(seconds) => mediaPlayer.seekTo(seconds)}
                scrollToSegmentIndex={scrollToSegmentIndex}
                activeSegmentIndex={activeSegmentIndex}
                annotations={annotations}
                onAnnotationsChange={handleAnnotationsChange}
                playerEnabled={playerEnabled}
                fileId={fileId}
                onTranscriptionChange={setTranscription}
                onPauseForAction={handlePauseForAction}
                onResumeAfterAction={handleResumeAfterAction}
                onPlaySegment={handlePlaySegment}
                playingSegmentIndex={playingSegmentIndex}
                onStopSegmentPlayback={mediaPlayer.stopSegmentPlayback}
                onTogglePlayer={() => {
                  setPlayerEnabled(prev => {
                    const next = !prev
                    if (next) {
                      // Ao ativar o player, inicia reprodução imediatamente
                      setTimeout(() => mediaPlayer.play(), 100)
                    } else {
                      // Ao desativar via toggle, reseta o player
                      mediaPlayer.reset()
                    }
                    return next
                  })
                }}
                mediaAvailable={mediaPlayer.mediaAvailable}
              />
            </TabPanel>

            <TabPanel value="metrics" current={currentTab}>
              <MetricsDashboard
                transcription={transcription}
                speakerMapping={speakerMapping}
                onNavigateToSegment={handleNavigateToSegment}
                fileId={fileId}
                processingTimes={processingTimes}
              />
            </TabPanel>

            <TabPanel value="sentiment" current={currentTab}>
              <SentimentAnalysisPanel
                fileId={fileId}
                onNavigateToSegment={handleNavigateToSegment}
                speakerMapping={speakerMapping}
                analysisTypeStatus={analysisStatus?.sentiment}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="entities" current={currentTab}>
              <EntityExtractionPanel
                fileId={fileId}
                onNavigateToSegment={handleNavigateToSegment}
                analysisTypeStatus={analysisStatus?.entities}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="topics" current={currentTab}>
              <TopicDetectionPanel
                fileId={fileId}
                analysisTypeStatus={analysisStatus?.topics}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                onNavigateToSegment={handleNavigateToSegment}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="timeline" current={currentTab}>
              <EventTimelinePanel
                fileId={fileId}
                onNavigateToSegment={handleNavigateToSegment}
                speakerMapping={speakerMapping}
                analysisTypeStatus={analysisStatus?.timeline}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="patterns" current={currentTab}>
              <SpeechPatternPanel
                fileId={fileId}
                onNavigateToSegment={handleNavigateToSegment}
                speakerMapping={speakerMapping}
                analysisTypeStatus={analysisStatus?.patterns}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="ai" current={currentTab}>
              <AISummaryPanel
                fileId={fileId}
                summaryStatus={analysisStatus?.summary}
                speakerMapping={speakerMapping}
                onSummaryGenerated={handleSummaryGenerated}
                onAnalysisStarted={handleOnDemandAnalysisStarted}
                analysisOutdated={analysisStatus?.analysisOutdated}
              />
            </TabPanel>

            <TabPanel value="export" current={currentTab}>
              <ExportPanel
                transcription={transcription}
                aiSummary={aiSummary}
                speakerMapping={speakerMapping}
                annotations={annotations}
              />
            </TabPanel>
          </Box>
        </Paper>
      </Container>

      {/* Player flutuante — visível apenas na aba de transcrição */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          bgcolor: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -2px 16px rgba(0, 0, 0, 0.08)',
          display: showFloatingPlayer ? 'block' : 'none',
          '& > *': {
            mb: '0 !important',
            borderRadius: '0 !important',
          },
        }}
      >
        <MediaPlayer
          player={mediaPlayer}
          segments={transcription?.transcript}
          speakerMapping={speakerMapping}
          onClose={handleClosePlayer}
        />
      </Box>
    </Box>
  )
}
